import { exclamationIcon, checkIcon, closeIcon, firmIcon, dotIcon } from './icons';

const statusMap = {
  passed: { icon: 'exclamation', bg: '#4A2825', color: '#F16C5B' },
  traded: { icon: 'check', bg: '#29462B', color: '#7BE081' },
  cancelled: { icon: 'close', bg: '#4A2825', color: '#F16C5B' },
  due_in: { icon: 'exclamation', bg: '#5C4A2D', color: '#F6C244' },
  firm_in: { icon: 'firm', bg: '#35356B', color: '#A89BFF' },
  expired: { icon: 'exclamation', bg: '#4A2825', color: '#F16C5B' },
};

// Helper function to format status text
function formatStatusText(status: string, dueTime?: string, firmTime?: string): string {
  const statusLower = (status || '').toString().toLowerCase();
  
  switch (statusLower) {
    case 'due_in': {
      if (dueTime) {
        const dueDate = new Date(dueTime);
        const now = new Date();
        const diffMs = dueDate.getTime() - now.getTime();
        const mins = Math.max(0, Math.floor(diffMs / 60000));
        if (mins > 90) {
          const hours = Math.floor(mins / 60);
          const remMins = mins % 60;
          return `Due in ${hours}h${remMins > 0 ? ` ${remMins}m` : ''}`;
        }
        return `Due in ${mins}m`;
      }
      return 'Due in';
    }
    case 'expired':
      return 'Completed - Expired';
    case 'traded':
      return 'Completed - Executed';
    case 'passed':
      return 'Completed - Passed';
    case 'cancelled':
      return 'Completed - Cancelled';
    case 'firm_in': {
      if (firmTime) {
        const firmDate = new Date(firmTime);
        const now = new Date();
        const diffMs = firmDate.getTime() - now.getTime();
        const mins = Math.max(0, Math.floor(diffMs / 60000));
        if (mins > 90) {
          const hours = Math.floor(mins / 60);
          const remMins = mins % 60;
          return `Firm until ${hours}h${remMins > 0 ? ` ${remMins}m` : ''}`;
        }
        return `Firm until ${mins}m`;
      }
      return 'Firm until';
    }
    default:
      return `Completed - ${status}`;
  }
}

// Helper function to get icon
function getIcon(icon: string, color: string): string {
  switch (icon) {
    case 'exclamation':
      return exclamationIcon(color);
    case 'dot':
      return dotIcon;
    case 'check':
      return checkIcon;
    case 'close':
      return closeIcon;
    case 'firm':
      return firmIcon;
    default:
      return '';
  }
}

export class BondStatusBadgeCellRenderer {
  status: string;
  text: string;
  eGui: HTMLDivElement;

  init(params) {
    let statusValue = params.value;
    if (typeof statusValue === 'object' && statusValue !== null) {
      statusValue = statusValue.status || statusValue.STATUS || '';
    }
    this.status = (statusValue || '').toString().toLowerCase();
    this.text = params.valueFormatted || params.value || '';
    
    // Don't create badge if value is blank
    if (!this.status || !this.text) {
      this.eGui = document.createElement('div');
      return;
    }
    
    this.eGui = document.createElement('div');
    this.eGui.className = 'badge';
    this.updateBadge(params);
  }

  getGui() {
    return this.eGui;
  }

  refresh(params) {
    let statusValue = params.value;
    if (typeof statusValue === 'object' && statusValue !== null) {
      statusValue = statusValue.status || statusValue.STATUS || '';
    }
    this.status = (statusValue || '').toString().toLowerCase();
    this.text = params.valueFormatted || params.value || '';
    
    // Don't create badge if value is blank
    if (!this.status || !this.text) {
      return false;
    }
    
    this.updateBadge(params);
    return true;
  }

  updateBadge(params) {
    const map = statusMap[this.status] || { icon: 'info', bg: '#888', color: '#fff' };
    this.eGui.style.background = map.bg;
    this.eGui.style.color = map.color;
    this.eGui.style.display = 'flex';
    this.eGui.style.alignItems = 'center';
    this.eGui.style.borderRadius = '20px';
    this.eGui.style.padding = '0 15px 0 10px';
    this.eGui.style.height = '28px';
    this.eGui.style.gap = '2px';
    this.eGui.style.justifyContent = 'flex-start';
    this.eGui.innerHTML = `
      <span class="icon">${getIcon(map.icon, map.color)}</span>
      <span style="padding-top:3px;">${this.getText(this.text, params)}</span>
    `;
  }

  static formatStatusBadge(status: string, dueTime?: string, firmTime?: string) {
    const statusLower = (status || '').toString().toLowerCase();
    const map = statusMap[statusLower] || { icon: 'info', bg: '#888', color: '#fff' };
    
    return {
      map,
      getText: () => formatStatusText(status, dueTime, firmTime),
      getFormattedText: () => formatStatusText(status, dueTime, firmTime),
      getIcon: (icon: string, color: string) => getIcon(icon, color)
    };
  }

  getText(text, params) {
    return formatStatusText(text, params?.data?.DUE_TIME, params?.data?.FIRM_TIME);
  }
}
